/**
 * PATH: packages/ui/src/kanban/components/card-detail/manualAttachmentDoc.ts
 * FILE_ID: UI-KANBAN-CMP-MANUAL-ATT-DOC-001
 * PURPOSE: Pure mappers that let the MANUAL card uploads section reuse the
 *          ARC-2 in-card document layer: CardAttachment (QDrive summary
 *          shape) → KanbanEmailAttachment (the overlay/version-rail wire
 *          shape), plus the "does the editor handle this kind" gate.
 * WHY:     Prod UAT 2026-07-04: §4.1 says EVERY Files-tab PDF/image opens
 *          in-card, but only email-derived chips got the editor mount —
 *          manual uploads (the only files on email-less cards) still
 *          forced a download. A manual upload IS a QDrive FileRecord, so
 *          `id` doubles as `qdrive_file_id` — versioned saves + the
 *          version rail work with zero backend change.
 * FLOW:
 *   ┌────────────────┐   ┌───────────────────────┐   ┌────────────────────┐
 *   │ CardAttachment │ → │ toDocAttachment (map) │ → │ overlay + version  │
 *   │ (manual upload)│   │ opensInDocEditor(gate)│   │ rail props         │
 *   └────────────────┘   └───────────────────────┘   └────────────────────┘
 * RELATED:
 *   - CardAttachmentsSection.tsx: the consumer (rows become openable).
 *   - CardFileChip.tsx styleFor: the shared pdf/image classifier reused here.
 */
import type { KanbanEmailAdapter, KanbanEmailAttachment } from '../../api'
import type { CardAttachment } from '../../types'
import { styleFor } from './CardFileChip'

/**
 * Map a manual card upload onto the overlay's attachment wire shape.
 * The upload endpoint mints a REAL QDrive FileRecord, so the row id IS
 * the qdrive file id — that is what unlocks versioned saves + the rail.
 * `download_url` is a PRESIGNED S3 URL (serialize_file_summary), which
 * the overlay byte-fetch detects by its absolute origin.
 */
export function toDocAttachment(file: CardAttachment): KanbanEmailAttachment {
  return {
    id: file.id,
    filename: file.filename,
    size: file.file_size,
    content_type: file.content_type || null,
    download_url: file.download_url,
    qdrive_file_id: file.id,
  }
}

/**
 * True when the primary click should open the in-card editor: the host
 * injected the overlay AND the file is a kind the editors handle.
 * Mirrors CardFilesRail.opensInCard so both Files-tab sections behave
 * identically (§4.1 invariant: every PDF/image opens inside the card).
 */
export function opensInDocEditor(
  file: CardAttachment,
  emailAdapter: KanbanEmailAdapter | undefined,
): boolean {
  if (!emailAdapter?.DocumentEditorOverlay) return false
  if (!file.download_url) return false
  const kind = styleFor(toDocAttachment(file)).meta.kind
  return kind === 'pdf' || kind === 'image'
}
